import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatRelativeTime } from "@/lib/time";
import { ChatTranscript, type MessageEntry } from "@/components/ChatTranscript";

type Run = {
  id: string;
  workflowName: string;
  prRef: string;
  status: string;
  pendingRequest: unknown | null;
  createdAt: string;
  updatedAt: string;
};

type BadgeVariant = "default" | "secondary" | "outline" | "destructive";

function runStatusVariant(status: string): BadgeVariant {
  switch (status) {
    case "completed":
      return "default";
    case "running":
    case "awaiting_human":
      return "secondary";
    case "failed":
      return "destructive";
    default:
      return "outline";
  }
}

type TranscriptState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; messages: MessageEntry[] }
  | { status: "error" };

type Props = {
  prRef: string;
  prTitle: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function PRRunHistoryModal({ prRef, prTitle, open, onOpenChange }: Props) {
  const [runs, setRuns] = useState<Run[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [transcripts, setTranscripts] = useState<Record<string, TranscriptState>>({});

  // "owner/name#number" → split at last "#"
  const hashIdx = prRef.lastIndexOf("#");
  const repoDisplay = hashIdx >= 0 ? prRef.slice(0, hashIdx) : prRef;
  const number = hashIdx >= 0 ? prRef.slice(hashIdx + 1) : "";

  useEffect(() => {
    if (!open) return;

    setRuns([]);
    setRunsError(null);
    setExpandedIds(new Set());
    setTranscripts({});
    setRunsLoading(true);

    let cancelled = false;
    const encoded = encodeURIComponent(prRef);

    fetch(`/api/runs?prRef=${encoded}`)
      .then((r) => {
        if (!r.ok) return r.text().then((t) => Promise.reject(new Error(t)));
        return r.json() as Promise<{ items: Run[] }>;
      })
      .then((data) => {
        if (!cancelled) setRuns(data.items);
      })
      .catch((e: unknown) => {
        if (!cancelled) setRunsError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setRunsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, prRef]);

  function fetchTranscript(runId: string) {
    setTranscripts((prev) => ({ ...prev, [runId]: { status: "loading" } }));
    fetch(`/api/sessions/${runId}/transcript`)
      .then((r) => {
        if (r.status === 404) return { messages: [] as MessageEntry[] };
        if (!r.ok) return r.text().then((t) => Promise.reject(new Error(t)));
        return r.json() as Promise<{ messages: MessageEntry[] }>;
      })
      .then((data) => {
        setTranscripts((prev) => ({
          ...prev,
          [runId]: { status: "loaded", messages: data.messages ?? [] },
        }));
      })
      .catch(() => {
        setTranscripts((prev) => ({ ...prev, [runId]: { status: "error" } }));
      });
  }

  function toggleRun(runId: string) {
    const isExpanding = !expandedIds.has(runId);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) {
        next.delete(runId);
      } else {
        next.add(runId);
      }
      return next;
    });
    if (isExpanding && !transcripts[runId]) {
      fetchTranscript(runId);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Runs — {repoDisplay}#{number}
          </DialogTitle>
          <DialogDescription>{prTitle}</DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-1">
          {runsLoading && (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          )}

          {!runsLoading && runsError && (
            <p className="py-2 text-sm text-destructive">{runsError}</p>
          )}

          {!runsLoading && !runsError && runs.length === 0 && (
            <p className="py-2 text-sm text-muted-foreground">No runs yet for this PR.</p>
          )}

          {runs.map((run) => {
            const expanded = expandedIds.has(run.id);
            const ts = transcripts[run.id];

            return (
              <div key={run.id} className="rounded-md border">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50"
                  onClick={() => toggleRun(run.id)}
                >
                  {expanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="font-mono">{run.workflowName}</span>
                  <Badge variant={runStatusVariant(run.status)} className="ml-1">
                    {run.status}
                  </Badge>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {formatRelativeTime(run.createdAt)}
                  </span>
                </button>

                {expanded && (
                  <div className="border-t px-3 py-2">
                    {(!ts || ts.status === "loading") && (
                      <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading…
                      </div>
                    )}
                    {ts?.status === "error" && (
                      <p className="py-2 text-sm text-destructive">
                        Failed to load transcript.
                      </p>
                    )}
                    {ts?.status === "loaded" && ts.messages.length === 0 && (
                      <p className="py-2 text-sm text-muted-foreground">
                        No log for this run.
                      </p>
                    )}
                    {ts?.status === "loaded" && ts.messages.length > 0 && (
                      <ChatTranscript messages={ts.messages} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
